//
// src/pages/Reports/PremiumWindowReportPage.js
//
// Premium Window Report UI
// ============================================================================
// PURPOSE
// -------
// This page presents premium-window analytics based on realized visits.
//
// A "premium window" is a recurring weekday-slot window that appears
// commercially strong not merely because of raw traffic, but because it tends
// to show:
// - stronger average ticket,
// - stronger revenue per booked hour,
// - stronger premium-visit mix,
// - stronger premium-revenue participation.
//
// WHY THIS PAGE MATTERS
// ---------------------
// Some windows are strategically valuable even when they are not the busiest.
// Management often needs to distinguish:
//
// 1) high-volume windows
// 2) high-value windows
// 3) high-quality premium windows
//
// This report is designed to make that distinction visible quickly.
//
// CURRENT BACKEND SHAPE
// --------------------
// Expected payload:
//
// {
//   period,
//   filters,
//   methodology,
//   premium_threshold,
//   summary,
//   rows,
//   top_premium_windows,
//   top_value_mix_windows
// }
//
// Typical row shape:
//
// {
//   scoped_slot_key,
//   weekday,
//   weekday_label,
//   slot_key,
//
//   total_visits,
//   total_booked_minutes,
//   total_allocated_revenue,
//
//   avg_revenue_per_visit,
//   revenue_per_booked_hour,
//
//   premium_visits_count,
//   premium_visit_share,
//   premium_revenue_share,
//
//   premium_window_score,
//   premium_window_class,
//
//   top_services,
//   top_categories,
//
//   recommendation
// }
//
// UI GOALS
// --------
// 1) Keep the page visually aligned with the rest of the reports module.
// 2) Preserve local filtering and sorting for speed and transparency.
// 3) Add executive charts similar to stronger report pages in the app.
// 4) Let management identify premium windows in seconds.
// 5) Support click-to-select interaction for charts and ranking table.
// 6) Provide a drilldown panel with immediate managerial interpretation.
// 7) Keep CSS local to this page.
//
// ARCHITECTURAL PRINCIPLES
// ------------------------
// - Business calculations remain backend-owned.
// - Frontend is responsible for normalization, chart preparation,
//   interpretation language, and local UI interactivity.
// - This page should not silently invent backend business metrics.
// - Heuristic executive classification is allowed only as a UI reading aid.
//

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchPremiumWindowReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from "recharts";

import "./PremiumWindowReportPage.css";

// I18N NOTE
// ---------
// This report intentionally keeps backend payload fields and classification
// codes stable. The React layer translates only frontend-owned labels, chart
// legends, tooltips, local insight texts, empty states, and fallback strings.
// Backend methodology note_keys are translated by the frontend, while the
// legacy backend notes array remains a safe fallback for older payloads.
// Row recommendations remain backend-owned payload text.

/* -------------------------------------------------------------------------- */
/* Small numeric / formatting helpers                                         */
/* -------------------------------------------------------------------------- */

/**
 * Convert unknown input into a finite number.
 * This prevents charts and summaries from breaking if backend returns strings,
 * nulls, empty values, or unexpected data.
 */
function toFiniteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Stable money formatting for report UI.
 */
function formatMoney(value) {
  return toFiniteNumber(value, 0).toFixed(2);
}

/**
 * Stable minute formatting.
 */
function formatMinutes(value) {
  return toFiniteNumber(value, 0).toFixed(1);
}

/**
 * Count-style formatting.
 */
function formatCount(value) {
  return String(Math.round(toFiniteNumber(value, 0)));
}

/**
 * Generic decimal formatting.
 */
function formatNumber(value) {
  return toFiniteNumber(value, 0).toFixed(2);
}

/**
 * Ratio formatter.
 * Example:
 * - 0.36 -> 36.0%
 * - 1.24 -> 124.0%
 */
function formatPercentFromRatio(value) {
  return `${(toFiniteNumber(value, 0) * 100).toFixed(1)}%`;
}

/**
 * Already-percent-like number formatter.
 * Example:
 * - 72.3 -> 72.3%
 */
function formatPercent(value) {
  return `${toFiniteNumber(value, 0).toFixed(1)}%`;
}

/**
 * Compact rendering of backend-provided "top list" arrays.
 */
function formatTopList(items) {
  if (!Array.isArray(items) || items.length === 0) return "—";
  return items.map((x) => `${x.name} (${x.count})`).join(", ");
}

/**
 * Badge class helper for premium window class.
 */
function premiumBadgeClassName(value) {
  const raw = String(value || "").toLowerCase();

  if (raw === "premium") return "premium-badge premium-badge--premium";
  if (raw === "strong") return "premium-badge premium-badge--strong";
  if (raw === "emerging") return "premium-badge premium-badge--emerging";
  return "premium-badge premium-badge--low";
}

/**
 * Translate backend premium-window class codes for display only.
 *
 * IMPORTANT:
 * The original backend code is still used for CSS and filtering. This function
 * must never normalize or alter the business code itself.
 */
function formatPremiumWindowClassLabel(value, t) {
  const raw = String(value || "").toLowerCase();

  if (raw === "premium") return t("reports:premiumWindow.classes.premium");
  if (raw === "strong") return t("reports:premiumWindow.classes.strong");
  if (raw === "emerging") return t("reports:premiumWindow.classes.emerging");
  return t("reports:premiumWindow.classes.low");
}

/* -------------------------------------------------------------------------- */
/* Chart palette                                                              */
/* -------------------------------------------------------------------------- */

const CHART_COLORS = Object.freeze({
  revenue: "#2563eb",
  revenueLight: "#60a5fa",
  premium: "#d97706",
  premiumLight: "#f59e0b",
  efficiency: "#16a34a",
  efficiencyLight: "#4ade80",
  mix: "#7c3aed",
  cumulative: "#f59e0b",
  weak: "#f97316",
  poor: "#dc2626",
  neutral: "#94a3b8",
  grid: "#e5e7eb",
  axis: "#6b7280",
});

/* -------------------------------------------------------------------------- */
/* Normalized KPI access helpers                                              */
/* -------------------------------------------------------------------------- */

function getRevenuePerBookedHour(row) {
  return toFiniteNumber(row?.revenue_per_booked_hour, 0);
}

function getAvgRevenuePerVisit(row) {
  return toFiniteNumber(row?.avg_revenue_per_visit, 0);
}

function getPremiumVisitShare(row) {
  return toFiniteNumber(row?.premium_visit_share, 0);
}

function getPremiumRevenueShare(row) {
  return toFiniteNumber(row?.premium_revenue_share, 0);
}

function getPremiumScore(row) {
  return toFiniteNumber(row?.premium_window_score, 0);
}

/* -------------------------------------------------------------------------- */
/* Executive classification helpers                                           */
/* -------------------------------------------------------------------------- */

function bubbleSizeFromValue(value, minValue, maxValue) {
  const v = toFiniteNumber(value, 0);
  const min = toFiniteNumber(minValue, 0);
  const max = toFiniteNumber(maxValue, 0);

  if (max <= min) return 160;

  const ratio = (v - min) / (max - min);
  return 110 + ratio * 260;
}

/**
 * Executive heuristic class.
 *
 * This is not a backend truth and not an accounting category.
 * It is merely a managerial UI layer helping decision-makers read the page.
 */
function getPremiumWindowExecutiveClass(row) {
  const score = getPremiumScore(row);
  const avgRevenuePerVisit = getAvgRevenuePerVisit(row);
  const revenuePerBookedHour = getRevenuePerBookedHour(row);
  const premiumVisitShare = getPremiumVisitShare(row);

  if (score >= 70 && avgRevenuePerVisit >= 220 && premiumVisitShare >= 0.28) {
    return "star";
  }

  if (revenuePerBookedHour >= 150 && avgRevenuePerVisit >= 180) {
    return "efficient";
  }

  if (score >= 60 && premiumVisitShare < 0.18) {
    return "volume";
  }

  if (avgRevenuePerVisit < 120 && premiumVisitShare < 0.12) {
    return "weak";
  }

  return "standard";
}

function getPremiumWindowExecutiveColor(executiveClass) {
  switch (executiveClass) {
    case "star":
      return CHART_COLORS.efficiency;
    case "efficient":
      return "#0ea5e9";
    case "volume":
      return CHART_COLORS.cumulative;
    case "weak":
      return CHART_COLORS.poor;
    case "standard":
    default:
      return CHART_COLORS.premium;
  }
}

function getPremiumWindowExecutiveExplanation(executiveClass, t) {
  switch (executiveClass) {
    case "star":
      return t("reports:premiumWindow.executiveExplanations.star");
    case "efficient":
      return t("reports:premiumWindow.executiveExplanations.efficient");
    case "volume":
      return t("reports:premiumWindow.executiveExplanations.volume");
    case "weak":
      return t("reports:premiumWindow.executiveExplanations.weak");
    case "standard":
    default:
      return t("reports:premiumWindow.executiveExplanations.standard");
  }
}

/* -------------------------------------------------------------------------- */
/* Executive interpretation helpers                                           */
/* -------------------------------------------------------------------------- */

function buildPremiumWindowInterpretation(row, t) {
  const avgRevenuePerVisit = getAvgRevenuePerVisit(row);
  const revenuePerBookedHour = getRevenuePerBookedHour(row);
  const premiumVisitShare = getPremiumVisitShare(row);
  const premiumRevenueShare = getPremiumRevenueShare(row);
  const score = getPremiumScore(row);

  if (
    score >= 80 &&
    avgRevenuePerVisit >= 220 &&
    premiumRevenueShare >= 0.35
  ) {
    return t("reports:premiumWindow.interpretations.highlyStrategic");
  }

  if (premiumVisitShare >= 0.30 && revenuePerBookedHour >= 150) {
    return t("reports:premiumWindow.interpretations.premiumAndProductive");
  }

  if (avgRevenuePerVisit >= 220 && premiumVisitShare < 0.15) {
    return t("reports:premiumWindow.interpretations.strongTicketNarrowMix");
  }

  if (score >= 60 && premiumRevenueShare >= 0.25) {
    return t("reports:premiumWindow.interpretations.premiumSupporting");
  }

  if (avgRevenuePerVisit < 120 && premiumVisitShare < 0.12) {
    return t("reports:premiumWindow.interpretations.commerciallyWeak");
  }

  return t("reports:premiumWindow.interpretations.middleBand");
}

function buildPremiumWindowHeadline(row, t) {
  return t("reports:premiumWindow.headline.premiumScore", {
    score: formatCount(getPremiumScore(row)),
  });
}

function buildPremiumWindowMeaning(row, t) {
  const score = getPremiumScore(row);
  const avgRevenuePerVisit = getAvgRevenuePerVisit(row);
  const premiumVisitShare = getPremiumVisitShare(row);
  const premiumRevenueShare = getPremiumRevenueShare(row);

  if (score >= 80 && premiumRevenueShare >= 0.35) {
    return t("reports:premiumWindow.meaning.protectedAsset");
  }

  if (score >= 70 && premiumVisitShare >= 0.25) {
    return t("reports:premiumWindow.meaning.broadPremium");
  }

  if (avgRevenuePerVisit >= 220 && premiumVisitShare < 0.15) {
    return t("reports:premiumWindow.meaning.highValueNarrow");
  }

  if (score <= 30 && avgRevenuePerVisit < 120) {
    return t("reports:premiumWindow.meaning.structurallyWeak");
  }

  return t("reports:premiumWindow.meaning.relevant");
}

function buildPremiumWindowAction(row, t) {
  const score = getPremiumScore(row);
  const premiumVisitShare = getPremiumVisitShare(row);
  const avgRevenuePerVisit = getAvgRevenuePerVisit(row);

  if (score >= 80 && premiumVisitShare >= 0.25) {
    return t("reports:premiumWindow.actions.protect");
  }

  if (score >= 60 && avgRevenuePerVisit >= 180) {
    return t("reports:premiumWindow.actions.reinforce");
  }

  if (avgRevenuePerVisit >= 220 && premiumVisitShare < 0.15) {
    return t("reports:premiumWindow.actions.preservePrice");
  }

  if (score <= 30) {
    return t("reports:premiumWindow.actions.doNotOverprotect");
  }

  return t("reports:premiumWindow.actions.monitor");
}

function getPremiumWindowDriverTone(row) {
  const score = getPremiumScore(row);
  const avgRevenuePerVisit = getAvgRevenuePerVisit(row);
  const premiumVisitShare = getPremiumVisitShare(row);

  if (score >= 80 && premiumVisitShare >= 0.25) return "power";
  if (score >= 60 && avgRevenuePerVisit >= 180) return "strong";
  if (avgRevenuePerVisit >= 220 && premiumVisitShare < 0.15) return "premium";
  if (score <= 30 && avgRevenuePerVisit < 120) return "weak";
  return "balanced";
}

function getPremiumWindowDriverToneLabel(tone, t) {
  switch (tone) {
    case "power":
      return t("reports:premiumWindow.driver.tones.power");
    case "strong":
      return t("reports:premiumWindow.driver.tones.strong");
    case "premium":
      return t("reports:premiumWindow.driver.tones.premium");
    case "weak":
      return t("reports:premiumWindow.driver.tones.weak");
    case "balanced":
    default:
      return t("reports:premiumWindow.driver.tones.balanced");
  }
}

/* -------------------------------------------------------------------------- */
/* Rich tooltips                                                              */
/* -------------------------------------------------------------------------- */

function PremiumWindowExcellenceTooltip({ active, payload, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">{row.label}</div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:premiumWindow.labels.premiumScore")}</span>
          <strong>{formatCount(row.premium_window_score)}</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.avgRevenuePerVisit")}</span>
          <strong>{formatMoney(row.avg_revenue_per_visit)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.revenuePerBookedHour")}</span>
          <strong>{formatMoney(row.revenue_per_booked_hour)} PLN/h</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.totalRevenue")}</span>
          <strong>{formatMoney(row.total_allocated_revenue)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.premiumVisitShare")}</span>
          <strong>{formatPercentFromRatio(row.premium_visit_share)}</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.premiumRevenueShare")}</span>
          <strong>{formatPercentFromRatio(row.premium_revenue_share)}</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.totalVisits")}</span>
          <strong>{formatCount(row.total_visits)}</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.executiveClass")}</span>
          <strong>{row.executiveClass}</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {getPremiumWindowExecutiveExplanation(row.executiveClass, t)}
      </div>
    </div>
  );
}

function PremiumWindowRhythmTooltip({ active, payload, label, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">{label}</div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:premiumWindow.labels.totalRevenue")}</span>
          <strong>{formatMoney(row.total_allocated_revenue)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.premiumScore")}</span>
          <strong>{formatCount(row.premium_window_score)}</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.avgRevenuePerVisit")}</span>
          <strong>{formatMoney(row.avg_revenue_per_visit)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.revenuePerBookedHour")}</span>
          <strong>{formatMoney(row.revenue_per_booked_hour)} PLN/h</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.premiumVisitShare")}</span>
          <strong>{formatPercentFromRatio(row.premium_visit_share)}</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.premiumRevenueShare")}</span>
          <strong>{formatPercentFromRatio(row.premium_revenue_share)}</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.totalVisits")}</span>
          <strong>{formatCount(row.total_visits)}</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.bookedMinutes")}</span>
          <strong>{formatCount(row.total_booked_minutes)}</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {buildPremiumWindowInterpretation(row, t)}
      </div>
    </div>
  );
}

function PremiumWindowConcentrationTooltip({ active, payload, label, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">{label}</div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:premiumWindow.labels.revenue")}</span>
          <strong>{formatMoney(row.total_allocated_revenue)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.revenueShare")}</span>
          <strong>{formatPercentFromRatio(row.share_of_total_revenue)}</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.cumulativeShare")}</span>
          <strong>{formatPercent(row.cumulative_revenue_share_percent)}</strong>
        </div>
        <div>
          <span>{t("reports:premiumWindow.labels.premiumScore")}</span>
          <strong>{formatCount(row.premium_window_score)}</strong>
        </div>
      </div>

      <div className="chart-tooltip__explanation">
        {t("reports:premiumWindow.insights.concentration.tooltipExplanation")}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Insight builders                                                           */
/* -------------------------------------------------------------------------- */

function buildPremiumWindowOverviewInsight(rows, t) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      headline: t("reports:premiumWindow.insights.overview.emptyHeadline"),
      interpretation: t("reports:premiumWindow.insights.overview.emptyInterpretation"),
      action: t("reports:premiumWindow.insights.overview.emptyAction"),
    };
  }

  const strongestByScore = [...rows].sort(
    (a, b) =>
      getPremiumScore(b) - getPremiumScore(a) ||
      getAvgRevenuePerVisit(b) - getAvgRevenuePerVisit(a) ||
      a.window_order - b.window_order
  )[0];

  const strongestByTicket = [...rows].sort(
    (a, b) =>
      getAvgRevenuePerVisit(b) - getAvgRevenuePerVisit(a) ||
      getPremiumScore(b) - getPremiumScore(a) ||
      a.window_order - b.window_order
  )[0];

  const weakestByScore = [...rows].sort(
    (a, b) =>
      getPremiumScore(a) - getPremiumScore(b) ||
      getAvgRevenuePerVisit(a) - getAvgRevenuePerVisit(b) ||
      a.window_order - b.window_order
  )[0];

  return {
    headline: t("reports:premiumWindow.insights.overview.headline", {
      scoreLeader: strongestByScore?.label || "—",
      ticketLeader: strongestByTicket?.label || "—",
    }),
    interpretation:
      strongestByScore?.label === strongestByTicket?.label
        ? t("reports:premiumWindow.insights.overview.sameLeader")
        : t("reports:premiumWindow.insights.overview.differentLeaders"),
    action: t("reports:premiumWindow.insights.overview.action", {
      scoreLeader:
        strongestByScore?.label ||
        t("reports:premiumWindow.insights.overview.strongestWindowFallback"),
      weakestWindow:
        weakestByScore?.label ||
        t("reports:premiumWindow.insights.overview.weakestWindowFallback"),
    }),
  };
}

function buildRevenueConcentrationInterpretation(paretoRows, t) {
  if (!Array.isArray(paretoRows) || paretoRows.length === 0) {
    return t("reports:premiumWindow.insights.concentration.empty");
  }

  const top1 = paretoRows[0];
  const top2 = paretoRows[1];
  const top3 = paretoRows[2];

  const top1Share = toFiniteNumber(top1?.share_of_total_revenue, 0);
  const top2Cum = toFiniteNumber(top2?.cumulative_revenue_share_percent, 0);
  const top3Cum = toFiniteNumber(top3?.cumulative_revenue_share_percent, 0);

  if (top1Share >= 0.22) {
    return t("reports:premiumWindow.insights.concentration.topOne", {
      window: top1?.label || "—",
    });
  }

  if (top2Cum >= 55) {
    return t("reports:premiumWindow.insights.concentration.topTwo");
  }

  if (top3Cum >= 75) {
    return t("reports:premiumWindow.insights.concentration.topThree");
  }

  return t("reports:premiumWindow.insights.concentration.spread");
}

function buildRevenueConcentrationInsight(paretoRows, t) {
  return {
    headline: t("reports:premiumWindow.insights.concentration.headline"),
    interpretation: buildRevenueConcentrationInterpretation(paretoRows, t),
    action: t("reports:premiumWindow.insights.concentration.action"),
  };
}

function buildExcellenceMatrixInsight(rows, t) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      headline: t("reports:premiumWindow.insights.excellence.emptyHeadline"),
      interpretation: t("reports:premiumWindow.insights.excellence.emptyInterpretation"),
      action: t("reports:premiumWindow.insights.excellence.emptyAction"),
    };
  }

  const stars = rows.filter((row) => row.executiveClass === "star");
  const weaks = rows.filter((row) => row.executiveClass === "weak");

  if (stars.length > 0 && weaks.length > 0) {
    return {
      headline: t("reports:premiumWindow.insights.excellence.starWeakHeadline", {
        starWindow: stars[0].label,
        weakWindow: weaks[0].label,
      }),
      interpretation: t("reports:premiumWindow.insights.excellence.starWeakInterpretation"),
      action: t("reports:premiumWindow.insights.excellence.starWeakAction"),
    };
  }

  if (stars.length > 0) {
    return {
      headline: t("reports:premiumWindow.insights.excellence.starHeadline", {
        starWindow: stars[0].label,
      }),
      interpretation: t("reports:premiumWindow.insights.excellence.starInterpretation"),
      action: t("reports:premiumWindow.insights.excellence.starAction"),
    };
  }

  return {
    headline: t("reports:premiumWindow.insights.excellence.balancedHeadline"),
    interpretation: t("reports:premiumWindow.insights.excellence.balancedInterpretation"),
    action: t("reports:premiumWindow.insights.excellence.balancedAction"),
  };
}

/* -------------------------------------------------------------------------- */
/* Driver drilldown panel                                                     */
/* -------------------------------------------------------------------------- */

function PremiumWindowDriverPanel({
  row,
  onSelectPrev,
  onSelectNext,
  hasPrev,
  hasNext,
  t,
}) {
  if (!row) {
    return (
      <div className="driver-panel driver-panel--empty">
        <div className="driver-panel__title">{t("reports:premiumWindow.driver.title")}</div>
<div className="driver-panel__empty">{t("reports:premiumWindow.driver.empty")}</div>
      </div>
    );
  }

  const tone = getPremiumWindowDriverTone(row);
  const toneLabel = getPremiumWindowDriverToneLabel(tone, t);

  return (
    <div className={`driver-panel driver-panel--${tone}`}>
      <div className="driver-panel__topbar">
        <div>
          <div className="driver-panel__eyebrow">{t("reports:premiumWindow.driver.eyebrow")}</div>
          <div className="driver-panel__title">{row.label}</div>
        </div>

        <div className="driver-panel__actions">
          <span className={`driver-tone-badge driver-tone-badge--${tone}`}>
            {toneLabel}
          </span>

          <button
            type="button"
            className="driver-nav-button"
            onClick={onSelectPrev}
            disabled={!hasPrev}
          >
            {t("reports:premiumWindow.driver.previous")}
          </button>

          <button
            type="button"
            className="driver-nav-button"
            onClick={onSelectNext}
            disabled={!hasNext}
          >
            {t("reports:premiumWindow.driver.next")}
          </button>
        </div>
      </div>

      <div className="driver-panel__headline">
        {buildPremiumWindowHeadline(row, t)}
      </div>

      <div className="driver-panel__subline">
{t("reports:premiumWindow.driver.subline")}
      </div>

      <div className="driver-grid">
        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:premiumWindow.labels.totalRevenue")}</span>
          <strong className="driver-metric-card__value">
            {formatMoney(row.total_allocated_revenue)} PLN
          </strong>
        </div>

        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:premiumWindow.labels.visits")}</span>
          <strong className="driver-metric-card__value">
            {formatCount(row.total_visits)}
          </strong>
        </div>

        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:premiumWindow.labels.bookedMinutes")}</span>
          <strong className="driver-metric-card__value">
            {formatCount(row.total_booked_minutes)}
          </strong>
        </div>

        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:premiumWindow.labels.avgRevenuePerVisit")}</span>
          <strong className="driver-metric-card__value">
            {formatMoney(row.avg_revenue_per_visit)} PLN
          </strong>
        </div>

        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:premiumWindow.labels.revenuePerBookedHour")}</span>
          <strong className="driver-metric-card__value">
            {formatMoney(row.revenue_per_booked_hour)} PLN/h
          </strong>
        </div>

        <div className="driver-metric-card">
          <span className="driver-metric-card__label">{t("reports:premiumWindow.labels.premiumVisits")}</span>
          <strong className="driver-metric-card__value">
            {formatCount(row.premium_visits_count)}
          </strong>
        </div>
      </div>

      <div className="driver-breakdown-grid">
        <div className="driver-breakdown-card">
          <div className="driver-breakdown-card__title">{t("reports:premiumWindow.labels.premiumSignal")}</div>
          <div className="driver-breakdown-card__text">
            {t("reports:premiumWindow.driver.premiumSignalText", {
              score: formatCount(row.premium_window_score),
              visitShare: formatPercentFromRatio(row.premium_visit_share),
              revenueShare: formatPercentFromRatio(row.premium_revenue_share),
            })}
          </div>
        </div>

        <div className="driver-breakdown-card">
          <div className="driver-breakdown-card__title">{t("reports:premiumWindow.labels.businessMeaning")}</div>
          <div className="driver-breakdown-card__text">
            {buildPremiumWindowMeaning(row, t)}
          </div>
        </div>

        <div className="driver-breakdown-card driver-breakdown-card--action">
          <div className="driver-breakdown-card__title">{t("reports:premiumWindow.labels.suggestedAction")}</div>
          <div className="driver-breakdown-card__text">
            {buildPremiumWindowAction(row, t)}
          </div>
        </div>
      </div>

      <div className="driver-footer-grid">
        <div className="driver-footer-item">
          <span>{t("reports:premiumWindow.labels.premiumVisitShare")}</span>
          <strong>{formatPercentFromRatio(row.premium_visit_share)}</strong>
        </div>

        <div className="driver-footer-item">
          <span>{t("reports:premiumWindow.labels.premiumRevenueShare")}</span>
          <strong>{formatPercentFromRatio(row.premium_revenue_share)}</strong>
        </div>

        <div className="driver-footer-item">
          <span>{t("reports:premiumWindow.labels.topServices")}</span>
          <strong>{formatTopList(row.top_services)}</strong>
        </div>

        <div className="driver-footer-item">
          <span>{t("reports:premiumWindow.labels.topCategories")}</span>
          <strong>{formatTopList(row.top_categories)}</strong>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const PremiumWindowReportPage = () => {
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  /**
   * Keep evaluation-mode policy aligned with other reports in the app.
   */
  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole, t]);

  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  /**
   * Page-local filters.
   *
   * userIds is backend-facing.
   * search, sorting, and showOnlyStrongPremium are frontend-facing.
   */
  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    searchText: "",
    showOnlyStrongPremium: false,
    sortBy: "premium_window_score",
    sortDir: "desc",
  }));

  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Tracks what period is actually loaded from backend.
   */
  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  /**
   * Selected premium window for executive drilldown.
   */
  const [selectedWindowId, setSelectedWindowId] = useState(null);

  /* ------------------------------------------------------------------------ */
  /* Keep userIds aligned for role === user                                   */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  /* ------------------------------------------------------------------------ */
  /* Load users for employee filter                                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[PremiumWindowReportPage] ❌ Failed to fetch users:", err);
        setError(t("reports:premiumWindow.errors.failedToLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  /* ------------------------------------------------------------------------ */
  /* Current user display helper                                              */
  /* ------------------------------------------------------------------------ */

  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser", { defaultValue: "Current user" });
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Fetch report                                                             */
  /* ------------------------------------------------------------------------ */

  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = await fetchPremiumWindowReport(from, to, userIds);
        setReport(payload || null);

        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error("[PremiumWindowReportPage] ❌ Error fetching report:", err);
        setError(t("reports:premiumWindow.errors.failedToFetch"));
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  /**
   * Stable initial request object prevents accidental first-load drift.
   */
  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  /* ------------------------------------------------------------------------ */
  /* Derived backend data                                                     */
  /* ------------------------------------------------------------------------ */

  const summary = report?.summary || {};
  const methodologyNotes = useMemo(() => {
    const methodology = report?.methodology || {};
    const reportKind = methodology.report_kind || "premium_window";

    /**
     * Preferred path for the i18n migration:
     * backend sends stable note_keys, frontend owns the visible language.
     * The legacy notes array remains supported so this page continues to work
     * against older backend payloads during staged deployments.
     */
    if (Array.isArray(methodology.note_keys) && methodology.note_keys.length > 0) {
      return methodology.note_keys.map((noteKey) => {
        const translatedNote = t(
          `reports:methodology.${reportKind}.notes.${noteKey}`,
          { defaultValue: "" }
        );

        return translatedNote || String(noteKey);
      });
    }

    return Array.isArray(methodology.notes) ? methodology.notes : [];
  }, [report, t]);

  const premiumThreshold = report?.premium_threshold || {};
  const rows = Array.isArray(report?.rows) ? report.rows : [];
  const topPremiumWindows = Array.isArray(report?.top_premium_windows)
    ? report.top_premium_windows
    : [];
  const topValueMixWindows = Array.isArray(report?.top_value_mix_windows)
    ? report.top_value_mix_windows
    : [];

  const hasReportData = Boolean(report);

  /* ------------------------------------------------------------------------ */
  /* Local search + sorting                                                   */
  /* ------------------------------------------------------------------------ */

  const filteredRows = useMemo(() => {
    let out = [...rows];

    if (filters.showOnlyStrongPremium) {
      out = out.filter((row) => {
        const cls = String(row.premium_window_class || "").toLowerCase();
        return cls === "strong" || cls === "premium";
      });
    }

    const searchText = String(filters.searchText || "").trim().toLowerCase();

    if (!searchText) return out;

    return out.filter((row) => {
      const weekday = String(row.weekday_label || "").toLowerCase();
      const slot = String(row.slot_key || "").toLowerCase();
      const topServices = formatTopList(row.top_services).toLowerCase();
      const topCategories = formatTopList(row.top_categories).toLowerCase();
      const recommendation = String(row.recommendation || "").toLowerCase();

      return (
        weekday.includes(searchText) ||
        slot.includes(searchText) ||
        topServices.includes(searchText) ||
        topCategories.includes(searchText) ||
        recommendation.includes(searchText)
      );
    });
  }, [rows, filters.showOnlyStrongPremium, filters.searchText]);

  const sortedRows = useMemo(() => {
    const out = [...filteredRows];
    const sortBy = filters.sortBy || "premium_window_score";
    const sortDir = filters.sortDir === "asc" ? "asc" : "desc";

    out.sort((a, b) => {
      let cmp = 0;

      switch (sortBy) {
        case "weekday_label":
          cmp = String(a.weekday_label || "").localeCompare(
            String(b.weekday_label || "")
          );
          break;

        case "slot_key":
          cmp = String(a.slot_key || "").localeCompare(String(b.slot_key || ""));
          break;

        case "avg_revenue_per_visit":
          cmp = getAvgRevenuePerVisit(a) - getAvgRevenuePerVisit(b);
          break;

        case "revenue_per_booked_hour":
          cmp = getRevenuePerBookedHour(a) - getRevenuePerBookedHour(b);
          break;

        case "premium_visit_share":
          cmp = getPremiumVisitShare(a) - getPremiumVisitShare(b);
          break;

        case "total_allocated_revenue":
          cmp =
            toFiniteNumber(a.total_allocated_revenue, 0) -
            toFiniteNumber(b.total_allocated_revenue, 0);
          break;

        case "premium_window_score":
        default:
          cmp = getPremiumScore(a) - getPremiumScore(b);
          break;
      }

      if (cmp === 0) {
        cmp = String(a.scoped_slot_key || "").localeCompare(
          String(b.scoped_slot_key || "")
        );
      }

      return sortDir === "asc" ? cmp : -cmp;
    });

    return out;
  }, [filteredRows, filters.sortBy, filters.sortDir]);

  /* ------------------------------------------------------------------------ */
  /* Row normalization for charts / drilldown                                 */
  /* ------------------------------------------------------------------------ */

  /**
   * Normalize rows into chart-safe numeric structures.
   *
   * IMPORTANT FIX:
   * The id must be stable across renders. It must not depend on the map index.
   * Using index here can invalidate selection and may contribute to re-render
   * loops when selection-repair effects try to "fix" the current choice.
   */
  const premiumWindowChartRows = useMemo(() => {
    return rows.map((row, index) => ({
      ...row,
      id: String(
        row?.scoped_slot_key ||
          `${row?.weekday || "x"}|${row?.slot_key || "x"}`
      ),
      label: `${row?.weekday_label || t("reports:common.unknown")} • ${row?.slot_key || t("reports:premiumWindow.labels.slot")}`,
      window_order: index,

      weekday: toFiniteNumber(row?.weekday, 0),
      slot_start_minutes: toFiniteNumber(row?.slot_start_minutes, 0),

      total_visits: toFiniteNumber(row?.total_visits, 0),
      total_booked_minutes: toFiniteNumber(row?.total_booked_minutes, 0),
      total_allocated_revenue: toFiniteNumber(row?.total_allocated_revenue, 0),

      avg_revenue_per_visit: toFiniteNumber(row?.avg_revenue_per_visit, 0),
      revenue_per_booked_hour: toFiniteNumber(row?.revenue_per_booked_hour, 0),

      premium_visits_count: toFiniteNumber(row?.premium_visits_count, 0),
      premium_visit_share: toFiniteNumber(row?.premium_visit_share, 0),
      premium_revenue_share: toFiniteNumber(row?.premium_revenue_share, 0),

      premium_window_score: toFiniteNumber(row?.premium_window_score, 0),

      /**
       * Frontend-only derived share used by the Pareto chart / tooltip.
       * If backend ever sends it directly, this normalization will still be safe.
       */
      share_of_total_revenue: toFiniteNumber(row?.share_of_total_revenue, 0),
    }));
  }, [rows, t]);

  /**
   * Fast row lookup map for table row -> normalized row selection.
   */
  const premiumWindowRowMap = useMemo(() => {
    const map = new Map();
    premiumWindowChartRows.forEach((row) => {
      map.set(String(row.scoped_slot_key), row);
    });
    return map;
  }, [premiumWindowChartRows]);

  /* ------------------------------------------------------------------------ */
  /* Executive chart datasets                                                 */
  /* ------------------------------------------------------------------------ */

  /**
   * Excellence matrix:
   * X = total revenue
   * Y = avg revenue per visit
   * bubble size = premium score
   */
  const premiumWindowExcellenceMatrixData = useMemo(() => {
    if (!premiumWindowChartRows.length) return [];

    const scoreValues = premiumWindowChartRows.map((row) => getPremiumScore(row));
    const minScore = Math.min(...scoreValues);
    const maxScore = Math.max(...scoreValues);

    return premiumWindowChartRows.map((row) => {
      const executiveClass = getPremiumWindowExecutiveClass(row);

      return {
        ...row,
        x: row.total_allocated_revenue,
        y: row.avg_revenue_per_visit,
        z: bubbleSizeFromValue(getPremiumScore(row), minScore, maxScore),
        executiveClass,
        color: getPremiumWindowExecutiveColor(executiveClass),
      };
    });
  }, [premiumWindowChartRows]);

  /**
   * Natural order for click navigation and overview charts.
   *
   * Prefer weekday + slot order instead of raw dataset order. This makes
   * navigation more stable and business-readable.
   */
  const premiumWindowOverviewData = useMemo(() => {
    return [...premiumWindowChartRows].sort((a, b) => {
      if (a.weekday !== b.weekday) return a.weekday - b.weekday;
      return a.slot_start_minutes - b.slot_start_minutes;
    });
  }, [premiumWindowChartRows]);

  /**
   * Pareto / concentration order by revenue.
   */
  const premiumWindowParetoData = useMemo(() => {
    const sorted = [...premiumWindowChartRows].sort(
      (a, b) =>
        toFiniteNumber(b.total_allocated_revenue, 0) -
          toFiniteNumber(a.total_allocated_revenue, 0) ||
        a.weekday - b.weekday ||
        a.slot_start_minutes - b.slot_start_minutes
    );

    const totalRevenue = sorted.reduce(
      (sum, row) => sum + toFiniteNumber(row.total_allocated_revenue, 0),
      0
    );

    let runningRevenue = 0;

    return sorted.map((row) => {
      runningRevenue += toFiniteNumber(row.total_allocated_revenue, 0);

      return {
        ...row,
        share_of_total_revenue:
          totalRevenue > 0
            ? toFiniteNumber(row.total_allocated_revenue, 0) / totalRevenue
            : 0,
        cumulative_revenue_share_percent:
          totalRevenue > 0 ? (runningRevenue / totalRevenue) * 100 : 0,
      };
    });
  }, [premiumWindowChartRows]);

  /* ------------------------------------------------------------------------ */
  /* Executive insights                                                       */
  /* ------------------------------------------------------------------------ */

  const excellenceInsight = useMemo(() => {
    return buildExcellenceMatrixInsight(premiumWindowExcellenceMatrixData, t);
  }, [premiumWindowExcellenceMatrixData, t]);

  const overviewInsight = useMemo(() => {
    return buildPremiumWindowOverviewInsight(premiumWindowOverviewData, t);
  }, [premiumWindowOverviewData, t]);

  const concentrationInsight = useMemo(() => {
    return buildRevenueConcentrationInsight(premiumWindowParetoData, t);
  }, [premiumWindowParetoData, t]);

  /* ------------------------------------------------------------------------ */
  /* Driver panel selection logic                                             */
  /* ------------------------------------------------------------------------ */

  /**
   * Stable drilldown order.
   */
  const drilldownRows = useMemo(() => {
    return [...premiumWindowChartRows].sort((a, b) => {
      if (a.weekday !== b.weekday) return a.weekday - b.weekday;
      return a.slot_start_minutes - b.slot_start_minutes;
    });
  }, [premiumWindowChartRows]);

  /**
   * Keep a lightweight stable dependency for the selection-repair effect.
   * We avoid depending only on the full array of objects when we only need ids.
   */
  const drilldownRowIds = useMemo(() => {
    return drilldownRows.map((row) => row.id);
  }, [drilldownRows]);

  /**
   * Default selection = strongest premium window by score, then revenue.
   */
  const defaultSelectedWindowId = useMemo(() => {
    if (!drilldownRows.length) return null;

    const strongest = [...drilldownRows].sort(
      (a, b) =>
        getPremiumScore(b) - getPremiumScore(a) ||
        toFiniteNumber(b.total_allocated_revenue, 0) -
          toFiniteNumber(a.total_allocated_revenue, 0) ||
        a.weekday - b.weekday ||
        a.slot_start_minutes - b.slot_start_minutes
    )[0];

    return strongest?.id || drilldownRows[0]?.id || null;
  }, [drilldownRows]);

  /**
   * IMPORTANT FIX:
   * This effect only updates selection when:
   * - there are no rows and selection must be cleared, or
   * - current selection is invalid, or
   * - current selection is empty and a default must be chosen
   *
   * It avoids repeated state writes with equivalent values.
   */
  useEffect(() => {
    if (!drilldownRowIds.length) {
      if (selectedWindowId !== null) {
        setSelectedWindowId(null);
      }
      return;
    }

    if (selectedWindowId && drilldownRowIds.includes(selectedWindowId)) {
      return;
    }

    if (
      defaultSelectedWindowId &&
      selectedWindowId !== defaultSelectedWindowId
    ) {
      setSelectedWindowId(defaultSelectedWindowId);
    }
  }, [drilldownRowIds, defaultSelectedWindowId, selectedWindowId]);

  const selectedWindowRow = useMemo(() => {
    return drilldownRows.find((row) => row.id === selectedWindowId) || null;
  }, [drilldownRows, selectedWindowId]);

  const selectedWindowIndex = useMemo(() => {
    return drilldownRows.findIndex((row) => row.id === selectedWindowId);
  }, [drilldownRows, selectedWindowId]);

  const canSelectPrev = selectedWindowIndex > 0;
  const canSelectNext =
    selectedWindowIndex >= 0 && selectedWindowIndex < drilldownRows.length - 1;

  const handleSelectPrevWindow = useCallback(() => {
    if (!canSelectPrev) return;
    setSelectedWindowId(drilldownRows[selectedWindowIndex - 1]?.id || null);
  }, [canSelectPrev, drilldownRows, selectedWindowIndex]);

  const handleSelectNextWindow = useCallback(() => {
    if (!canSelectNext) return;
    setSelectedWindowId(drilldownRows[selectedWindowIndex + 1]?.id || null);
  }, [canSelectNext, drilldownRows, selectedWindowIndex]);

  const handleSelectWindowRow = useCallback(
    (row) => {
      const nextId = row?.id || null;
      if (!nextId || nextId === selectedWindowId) return;
      setSelectedWindowId(nextId);
    },
    [selectedWindowId]
  );

  /**
   * Recharts click payloads vary slightly by chart type.
   * Centralize the safe resolver.
   */
  const resolveClickedChartRow = useCallback((chartState) => {
    if (!chartState) return null;

    if (chartState?.activePayload?.[0]?.payload) {
      return chartState.activePayload[0].payload;
    }

    if (chartState?.payload) {
      return chartState.payload;
    }

    return null;
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Small render helper for top-card sections                                */
  /* ------------------------------------------------------------------------ */

  const renderWindowCards = (items, modifierClass) => (
    <div className="window-card-grid">
      {items.map((row, index) => (
        <div
          className={`window-card ${modifierClass}`}
          key={`${modifierClass}-${row.scoped_slot_key || index}`}
        >
          <div className="window-card__header">
            <div className="window-card__title">
              {row.weekday_label} • {row.slot_key}
            </div>
            <span className={premiumBadgeClassName(row.premium_window_class)}>
              {formatPremiumWindowClassLabel(row.premium_window_class, t)}
            </span>
          </div>

          <div className="window-card__metrics">
            <div>
              <span>{t("reports:premiumWindow.labels.premiumScore")}</span>
              <strong>{formatCount(row.premium_window_score)}</strong>
            </div>
            <div>
              <span>{t("reports:premiumWindow.labels.avgRevenuePerVisit")}</span>
              <strong>{formatMoney(row.avg_revenue_per_visit)} PLN</strong>
            </div>
            <div>
              <span>{t("reports:premiumWindow.labels.revenuePerBookedHour")}</span>
              <strong>{formatMoney(row.revenue_per_booked_hour)} PLN/h</strong>
            </div>
            <div>
              <span>{t("reports:premiumWindow.labels.premiumVisitShare")}</span>
              <strong>{formatPercentFromRatio(row.premium_visit_share)}</strong>
            </div>
          </div>

          <div className="window-card__block">
            <div className="window-card__label">{t("reports:premiumWindow.labels.topServices")}</div>
            <div className="window-card__text">
              {formatTopList(row.top_services)}
            </div>
          </div>

          <div className="window-card__block">
            <div className="window-card__label">{t("reports:premiumWindow.labels.recommendation")}</div>
            <div className="window-card__text">{row.recommendation}</div>
          </div>
        </div>
      ))}
    </div>
  );

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="premium-window-page">
      <div className="premium-window-header">
        <div className="header-left">
          <h1>{t("reports:premiumWindow.title")}</h1>
          <p className="header-hint">
            Identify weekday-slot windows with stronger-than-average value mix,
            not only stronger traffic.
          </p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="filters-bar">
        <div className="filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}

          <div className="filter-item filter-item-search">
            <label className="filter-label">{t("reports:common.search")}</label>
            <input
              type="text"
              className="search-input"
              value={filters.searchText}
              placeholder={t("reports:premiumWindow.searchPlaceholder")}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  searchText: e.target.value,
                }))
              }
            />
          </div>

          <div className="filter-item filter-item-toggle">
            <label className="filter-label">{t("reports:premiumWindow.focus")}</label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={filters.showOnlyStrongPremium}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    showOnlyStrongPremium: e.target.checked,
                  }))
                }
              />
              <span>{t("reports:premiumWindow.showOnlyStrongPremiumWindows")}</span>
            </label>
          </div>

          <div className="filter-item filter-item-sort">
            <label className="filter-label">{t("reports:premiumWindow.sortBy")}</label>
            <select
              className="sort-select"
              value={filters.sortBy}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortBy: e.target.value,
                }))
              }
            >
              <option value="premium_window_score">{t("reports:premiumWindow.sort.premiumScore")}</option>
              <option value="total_allocated_revenue">{t("reports:premiumWindow.sort.totalRevenue")}</option>
              <option value="avg_revenue_per_visit">{t("reports:premiumWindow.sort.avgRevenuePerVisit")}</option>
              <option value="revenue_per_booked_hour">{t("reports:premiumWindow.sort.revenuePerBookedHour")}</option>
              <option value="premium_visit_share">{t("reports:premiumWindow.sort.premiumVisitShare")}</option>
              <option value="weekday_label">{t("reports:premiumWindow.sort.weekday")}</option>
              <option value="slot_key">{t("reports:premiumWindow.sort.slot")}</option>
            </select>
          </div>

          <div className="filter-item filter-item-sortdir">
            <label className="filter-label">{t("reports:premiumWindow.direction")}</label>
            <select
              className="sort-select"
              value={filters.sortDir}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortDir: e.target.value,
                }))
              }
            >
              <option value="desc">{t("reports:premiumWindow.sort.descending")}</option>
              <option value="asc">{t("reports:premiumWindow.sort.ascending")}</option>
            </select>
          </div>
        </div>

        <div className="filters-actions">
          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>
        </div>
      </div>

      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : hasReportData ? (
          <>
            <div className="summary-grid">
              <div className="summary-card">
                <span className="summary-label">{t("reports:premiumWindow.summary.analyzedWindows")}</span>
                <span className="summary-value">
                  {formatCount(summary.analyzed_windows_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:premiumWindow.summary.totalVisits")}</span>
                <span className="summary-value">
                  {formatCount(summary.total_visits)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:premiumWindow.summary.allocatedRevenue")}</span>
                <span className="summary-value">
                  {formatMoney(summary.total_allocated_revenue)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:premiumWindow.summary.premiumWindows")}</span>
                <span className="summary-value">
                  {formatCount(summary.premium_windows_count)}
                </span>
              </div>
            </div>

            <div className="threshold-grid">
              <div className="threshold-card">
                <div className="threshold-title">{t("reports:premiumWindow.threshold.premiumThreshold")}</div>
                <div className="threshold-value">
                  {formatMoney(premiumThreshold.premium_threshold_value)} PLN
                </div>
                <div className="threshold-help">
                  Based on the 75th percentile of realized visit value.
                </div>
              </div>

              <div className="threshold-card">
                <div className="threshold-title">{t("reports:premiumWindow.threshold.globalAvgRevenuePerVisit")}</div>
                <div className="threshold-value">
                  {formatMoney(premiumThreshold.global_avg_revenue_per_visit)} PLN
                </div>
                <div className="threshold-help">
                  Used as a benchmark for relative premium-window strength.
                </div>
              </div>

              <div className="threshold-card">
                <div className="threshold-title">{t("reports:premiumWindow.threshold.topPremiumWindow")}</div>
                {summary?.top_premium_window ? (
                  <>
                    <div className="threshold-name">
                      {summary.top_premium_window.weekday_label} •{" "}
                      {summary.top_premium_window.slot_key}
                    </div>
                    <div className="threshold-help">
                      Score {formatCount(summary.top_premium_window.premium_window_score)}
                    </div>
                  </>
                ) : (
                  <div className="threshold-help">{t("reports:common.noDataAvailable")}</div>
                )}
              </div>

              <div className="threshold-card">
                <div className="threshold-title">{t("reports:premiumWindow.threshold.topValueMixWindow")}</div>
                {summary?.top_value_mix_window ? (
                  <>
                    <div className="threshold-name">
                      {summary.top_value_mix_window.weekday_label} •{" "}
                      {summary.top_value_mix_window.slot_key}
                    </div>
                    <div className="threshold-help">
                      {formatMoney(summary.top_value_mix_window.avg_revenue_per_visit)} PLN{" "}
                      per visit
                    </div>
                  </>
                ) : (
                  <div className="threshold-help">{t("reports:common.noDataAvailable")}</div>
                )}
              </div>
            </div>

            <div className="methodology-card">
              <div className="methodology-title">{t("reports:premiumWindow.methodology")}</div>
              <ul className="methodology-list">
                {methodologyNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>

              {(loadedPeriodMeta?.from || loadedPeriodMeta?.to) && (
                <div className="loaded-period-note">
                  {t("reports:common.loadedPeriod")}: <strong>{loadedPeriodMeta.from}</strong> →{" "}
                  <strong>{loadedPeriodMeta.to}</strong>
                  {loadedPeriodMeta.displayLabel ? (
                    <> ({loadedPeriodMeta.displayLabel})</>
                  ) : null}
                </div>
              )}
            </div>

            <div className="charts-grid">
              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:premiumWindow.charts.excellenceMatrixTitle")}</h2>
                  <p>
                    Compare premium-window scale and quality at the same time.
                    Horizontal axis shows total allocated revenue, vertical axis
                    shows average revenue per visit, and bubble size reflects the
                    premium-window score.
                  </p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart
                      margin={{ top: 10, right: 24, bottom: 12, left: 8 }}
                      onClick={(state) => {
                        const clickedRow = resolveClickedChartRow(state);
                        if (clickedRow) handleSelectWindowRow(clickedRow);
                      }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        type="number"
                        dataKey="x"
                        name={t("reports:premiumWindow.chart.revenue")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${Math.round(value)}`}
                        label={{
                          value: t("reports:premiumWindow.chart.allocatedRevenuePln"),
                          position: "insideBottom",
                          offset: -4,
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <YAxis
                        type="number"
                        dataKey="y"
                        name={t("reports:premiumWindow.chart.avgRevenuePerVisit")}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        label={{
                          value: t("reports:premiumWindow.chart.avgRevenuePerVisitPln"),
                          angle: -90,
                          position: "insideLeft",
                          fill: CHART_COLORS.axis,
                        }}
                      />

                      <ZAxis type="number" dataKey="z" range={[90, 340]} />
                      <Tooltip content={<PremiumWindowExcellenceTooltip t={t} />} />
                      <Legend />

                      <ReferenceLine
                        y={toFiniteNumber(premiumThreshold.global_avg_revenue_per_visit, 0)}
                        stroke="#cbd5e1"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <Scatter name={t("reports:premiumWindow.chart.premiumWindows")} data={premiumWindowExcellenceMatrixData}>
                        {premiumWindowExcellenceMatrixData.map((entry) => (
                          <Cell
                            key={`premium-window-excellence-${entry.id}`}
                            fill={entry.color}
                            stroke={entry.id === selectedWindowId ? "#111827" : "transparent"}
                            strokeWidth={entry.id === selectedWindowId ? 2 : 0}
                          />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-legend-note">
                  <span className="legend-chip legend-chip--premium">{t("reports:premiumWindow.classes.star")}</span>
                  <span className="legend-chip legend-chip--value-mix">{t("reports:premiumWindow.classes.efficient")}</span>
                  <span className="legend-chip legend-chip--strong">{t("reports:premiumWindow.classes.volume")}</span>
                  <span className="legend-chip legend-chip--emerging">{t("reports:premiumWindow.classes.weak")}</span>
                </div>

                <div className="insight-card">
                  <div className="insight-card__title">{excellenceInsight.headline}</div>
                  <div className="insight-card__text">{excellenceInsight.interpretation}</div>
                  <div className="insight-card__action">
                    <strong>{t("reports:premiumWindow.insights.suggestedAction")}</strong> {excellenceInsight.action}
                  </div>
                </div>
              </div>

              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:premiumWindow.charts.overviewTitle")}</h2>
                  <p>
                    Bars show total allocated revenue, while the line shows
                    premium-window score. This is the fastest executive read of
                    scale versus premium quality.
                  </p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={premiumWindowOverviewData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                      onClick={(state) => {
                        const clickedRow = resolveClickedChartRow(state);
                        if (clickedRow) handleSelectWindowRow(clickedRow);
                      }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        interval={0}
                        angle={-18}
                        textAnchor="end"
                        height={70}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <Tooltip content={<PremiumWindowRhythmTooltip t={t} />} />
                      <Legend />

                      <Bar
                        yAxisId="left"
                        dataKey="total_allocated_revenue"
                        name={t("reports:premiumWindow.chart.revenue")}
                        fill={CHART_COLORS.revenue}
                        radius={[6, 6, 0, 0]}
                      >
                        {premiumWindowOverviewData.map((entry) => (
                          <Cell
                            key={`premium-window-overview-bar-${entry.id}`}
                            fill={CHART_COLORS.revenue}
                            stroke={entry.id === selectedWindowId ? "#111827" : "transparent"}
                            strokeWidth={entry.id === selectedWindowId ? 2 : 0}
                          />
                        ))}
                      </Bar>

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="premium_window_score"
                        name={t("reports:premiumWindow.chart.premiumScore")}
                        stroke={CHART_COLORS.mix}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: CHART_COLORS.mix }}
                        activeDot={{ r: 5, fill: CHART_COLORS.mix }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                <div className="insight-card">
                  <div className="insight-card__title">{overviewInsight.headline}</div>
                  <div className="insight-card__text">{overviewInsight.interpretation}</div>
                  <div className="insight-card__action">
                    <strong>{t("reports:premiumWindow.insights.suggestedAction")}</strong> {overviewInsight.action}
                  </div>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:premiumWindow.charts.valueQualityTitle")}</h2>
                  <p>
                    Compare average revenue per visit with premium visit share.
                    Useful for understanding whether premium positioning is broad
                    or driven by a smaller number of expensive treatments.
                  </p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={premiumWindowOverviewData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                      onClick={(state) => {
                        const clickedRow = resolveClickedChartRow(state);
                        if (clickedRow) handleSelectWindowRow(clickedRow);
                      }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        interval={0}
                        angle={-18}
                        textAnchor="end"
                        height={70}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 1]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${Math.round(value * 100)}%`}
                      />

                      <Tooltip content={<PremiumWindowRhythmTooltip t={t} />} />
                      <Legend />

                      <Bar
                        yAxisId="left"
                        dataKey="avg_revenue_per_visit"
                        name={t("reports:premiumWindow.chart.avgRevenuePerVisit")}
                        fill={CHART_COLORS.premiumLight}
                        radius={[6, 6, 0, 0]}
                      >
                        {premiumWindowOverviewData.map((entry) => (
                          <Cell
                            key={`premium-window-value-share-bar-${entry.id}`}
                            fill={CHART_COLORS.premiumLight}
                            stroke={entry.id === selectedWindowId ? "#111827" : "transparent"}
                            strokeWidth={entry.id === selectedWindowId ? 2 : 0}
                          />
                        ))}
                      </Bar>

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="premium_visit_share"
                        name={t("reports:premiumWindow.chart.premiumVisitShare")}
                        stroke={CHART_COLORS.efficiency}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: CHART_COLORS.efficiency }}
                        activeDot={{ r: 5, fill: CHART_COLORS.efficiency }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:premiumWindow.charts.concentrationTitle")}</h2>
                  <p>{t("reports:premiumWindow.charts.concentrationDescription")}</p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={premiumWindowParetoData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                      onClick={(state) => {
                        const clickedRow = resolveClickedChartRow(state);
                        if (clickedRow) handleSelectWindowRow(clickedRow);
                      }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />

                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        interval={0}
                        angle={-18}
                        textAnchor="end"
                        height={70}
                      />

                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />

                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${value}%`}
                      />

                      <Tooltip content={<PremiumWindowConcentrationTooltip t={t} />} />
                      <Legend />

                      <ReferenceLine
                        yAxisId="right"
                        y={80}
                        stroke="#94a3b8"
                        strokeDasharray="4 4"
                        ifOverflow="extendDomain"
                      />

                      <Bar
                        yAxisId="left"
                        dataKey="total_allocated_revenue"
                        name={t("reports:premiumWindow.chart.revenue")}
                        fill={CHART_COLORS.revenue}
                        radius={[6, 6, 0, 0]}
                      >
                        {premiumWindowParetoData.map((entry) => (
                          <Cell
                            key={`premium-window-pareto-bar-${entry.id}`}
                            fill={CHART_COLORS.revenue}
                            stroke={entry.id === selectedWindowId ? "#111827" : "transparent"}
                            strokeWidth={entry.id === selectedWindowId ? 2 : 0}
                          />
                        ))}
                      </Bar>

                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="cumulative_revenue_share_percent"
                        name={t("reports:premiumWindow.chart.cumulativeShare")}
                        stroke={CHART_COLORS.cumulative}
                        strokeWidth={2}
                        dot={{ r: 2, fill: CHART_COLORS.cumulative }}
                        activeDot={{ r: 5, fill: CHART_COLORS.cumulative }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                <div className="insight-card">
                  <div className="insight-card__title">{concentrationInsight.headline}</div>
                  <div className="insight-card__text">{concentrationInsight.interpretation}</div>
                  <div className="insight-card__action">
                    <strong>{t("reports:premiumWindow.insights.suggestedAction")}</strong> {concentrationInsight.action}
                  </div>
                </div>
              </div>
            </div>

            <PremiumWindowDriverPanel
              row={selectedWindowRow}
              onSelectPrev={handleSelectPrevWindow}
              onSelectNext={handleSelectNextWindow}
              hasPrev={canSelectPrev}
              hasNext={canSelectNext}
              t={t}
            />

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:premiumWindow.charts.topPremiumWindowsTitle")}</h2>
                <p>{t("reports:premiumWindow.charts.topPremiumWindowsDescription")}</p>
              </div>

              {renderWindowCards(topPremiumWindows, "window-card--premium")}
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:premiumWindow.charts.topValueMixWindowsTitle")}</h2>
                <p>{t("reports:premiumWindow.charts.topValueMixWindowsDescription")}</p>
              </div>

              {renderWindowCards(topValueMixWindows, "window-card--value-mix")}
            </div>

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:premiumWindow.charts.detailedRankingTitle")}</h2>
                <p>{t("reports:premiumWindow.charts.detailedRankingDescription")}</p>
              </div>

              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:premiumWindow.labels.weekday")}</th>
                      <th>{t("reports:premiumWindow.labels.slot")}</th>
                      <th>{t("reports:premiumWindow.labels.class")}</th>
                      <th>{t("reports:premiumWindow.table.score")}</th>
                      <th>{t("reports:premiumWindow.labels.visits")}</th>
                      <th>{t("reports:premiumWindow.table.revenuePln")}</th>
                      <th>{t("reports:premiumWindow.table.avgRevenuePerVisit")}</th>
                      <th>{t("reports:premiumWindow.table.revenuePerBookedHour")}</th>
                      <th>{t("reports:premiumWindow.table.premiumVisits")}</th>
                      <th>{t("reports:premiumWindow.table.premiumVisitShare")}</th>
                      <th>{t("reports:premiumWindow.table.premiumRevenueShare")}</th>
                      <th>{t("reports:premiumWindow.labels.topServices")}</th>
                      <th>{t("reports:premiumWindow.labels.topCategories")}</th>
                      <th>{t("reports:premiumWindow.labels.recommendation")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.length > 0 ? (
                      sortedRows.map((row, index) => {
                        const normalizedRow =
                          premiumWindowRowMap.get(String(row.scoped_slot_key)) || {
                            ...row,
                            id: String(
                              row?.scoped_slot_key ||
                                `${row?.weekday || "x"}|${row?.slot_key || "x"}`
                            ),
                          };

                        const isSelected = normalizedRow?.id === selectedWindowId;

                        return (
                          <tr
                            key={
                              row.scoped_slot_key ||
                              `${row.weekday_label}-${row.slot_key}-${index}`
                            }
                            className={isSelected ? "table-row-selected" : ""}
                            onClick={() =>
                              normalizedRow && handleSelectWindowRow(normalizedRow)
                            }
                          >
                            <td>{row.weekday_label}</td>
                            <td>{row.slot_key}</td>
                            <td>
                              <span className={premiumBadgeClassName(row.premium_window_class)}>
                                {formatPremiumWindowClassLabel(row.premium_window_class, t)}
                              </span>
                            </td>
                            <td>{formatCount(row.premium_window_score)}</td>
                            <td>{formatCount(row.total_visits)}</td>
                            <td>{formatMoney(row.total_allocated_revenue)}</td>
                            <td>{formatMoney(row.avg_revenue_per_visit)}</td>
                            <td>{formatMoney(row.revenue_per_booked_hour)} PLN/h</td>
                            <td>{formatCount(row.premium_visits_count)}</td>
                            <td>{formatPercentFromRatio(row.premium_visit_share)}</td>
                            <td>{formatPercentFromRatio(row.premium_revenue_share)}</td>
                            <td className="wrap-cell">{formatTopList(row.top_services)}</td>
                            <td className="wrap-cell">{formatTopList(row.top_categories)}</td>
                            <td className="wrap-cell">{row.recommendation}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={14} className="empty-table-cell">
                          {t("reports:premiumWindow.noRowsMatchFilters")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}
      </div>
    </div>
  );
};

export default PremiumWindowReportPage;

